import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { Moeda, MOEDA_CONFIG } from '../../../enums/moeda.enum';
import { TipoConta } from '../../../enums/TipoConta.enum';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { ContaService } from '../../../services/conta/conta.service';
import { moedasCompativeis } from '../../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-dashboard-minhas-contas',
  templateUrl: './dashboard-minhas-contas.component.html',
  styleUrls: ['./dashboard-minhas-contas.component.scss'],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardMinhasContasComponent implements OnInit, OnDestroy, OnChanges {

  @Input() contas: ContaDTO[] = [];
  @Input() loading = false;
  @Input() currencySymbol = 'PYG';
  @Input() currencyDigits = '1.0-0';
  @Input() currencyLocale = 'es-PY';
  /** Moeda padrao da empresa (enum string: GUARANI, REAL, DOLAR) para pre-selecionar no dialog de nova conta */
  @Input() moedaPadraoEmpresa: string | undefined;

  /** Emitido quando uma conta e excluida, para que o pai recarregue os dados */
  @Output() contaExcluida = new EventEmitter<void>();
  /** Emitido quando uma conta e criada, para que o pai recarregue os dados */
  @Output() contaCriada = new EventEmitter<void>();

  // Dialog de contas
  showDialogContas = false;
  contasDialog: ContaDTO[] = [];

  // Dialog de nova conta
  showDialogNovaConta = false;
  novaContaForm!: FormGroup;
  moedaOptions: { label: string; value: string; flagUrl: string }[] = [];
  salvandoConta = false;

  // Dialog de confirmacao de exclusao
  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  texts = {
    minhas_contas: '',
    ver_todos: '',
    conta: '',
    saldo: '',
    fechar: '',
    total_receitas: '',
    total_despesas: '',
    tipo_conta: '',
    nova_conta: '',
    confirmar_exclusao: '',
    confirmacao: '',
    confirmar: '',
    cancelar: '',
    salvar: '',
    moeda: '',
    saldo_inicial: '',
    titulo_da_conta: '',
    numero_da_conta: '',
    campo_obrigatorio: '',
    conta_criada_sucesso: '',
    erro_criar_conta: '',
    selecione: ''
  };

  tooltips = {
    editar: '',
    excluir: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private contaService: ContaService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initNovaContaForm();
    this.loadTranslations();
    this.buildMoedaOptions();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildMoedaOptions();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando a moeda padrao da empresa muda (input do componente pai),
    // re-monta o dropdown aplicando o filtro de pareamento.
    if (changes['moedaPadraoEmpresa']) {
      this.buildMoedaOptions();
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  /**
   * Conjunto de moedas permitidas para a empresa logada, baseado na
   * matriz de pareamento (moedasCompativeis). Diferente das telas de
   * cadastro, aqui a moeda padrao vem via @Input (`moedaPadraoEmpresa`),
   * nao via getSessao(). Fallback: enquanto o input nao chegou, retorna
   * todas as 4 para nao bloquear UI.
   */
  get moedasPermitidas(): Set<string> {
    if (!this.moedaPadraoEmpresa) {
      return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    }
    const moeda = String(this.moedaPadraoEmpresa).toUpperCase();
    return new Set(moedasCompativeis(moeda as any));
  }

  private loadTranslations(): void {
    this.texts = {
      minhas_contas: this.translate.instant('dashboard.texts.minhas_contas'),
      ver_todos: this.translate.instant('dashboard.texts.ver_todos'),
      conta: this.translate.instant('dashboard.texts.conta'),
      saldo: this.translate.instant('dashboard.texts.saldo'),
      fechar: this.translate.instant('dashboard.texts.fechar'),
      total_receitas: this.translate.instant('dashboard.texts.total_receitas'),
      total_despesas: this.translate.instant('dashboard.texts.total_despesas'),
      tipo_conta: this.translate.instant('dashboard.texts.tipo_conta'),
      nova_conta: this.translate.instant('dashboard.texts.nova_conta'),
      confirmar_exclusao: this.translate.instant('dashboard.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar: this.translate.instant('common.confirmar'),
      cancelar: this.translate.instant('common.cancelar'),
      salvar: this.translate.instant('dashboard.texts.nova_conta_form.salvar'),
      moeda: this.translate.instant('dashboard.texts.nova_conta_form.moeda'),
      saldo_inicial: this.translate.instant('dashboard.texts.nova_conta_form.saldo_inicial'),
      titulo_da_conta: this.translate.instant('dashboard.texts.nova_conta_form.titulo_da_conta'),
      numero_da_conta: this.translate.instant('dashboard.texts.nova_conta_form.numero_da_conta'),
      campo_obrigatorio: this.translate.instant('dashboard.texts.nova_conta_form.campo_obrigatorio'),
      conta_criada_sucesso: this.translate.instant('dashboard.texts.nova_conta_form.conta_criada_sucesso'),
      erro_criar_conta: this.translate.instant('dashboard.texts.nova_conta_form.erro_criar_conta'),
      selecione: this.translate.instant('dashboard.texts.nova_conta_form.selecione')
    };

    this.tooltips = {
      editar: this.translate.instant('contas.tooltips.editar'),
      excluir: this.translate.instant('contas.tooltips.excluir')
    };
  }

  /** Inicializa o FormGroup de nova conta */
  private initNovaContaForm(): void {
    this.novaContaForm = new FormGroup({
      moeda: new FormControl(null, Validators.required),
      saldoInicial: new FormControl(0),
      tituloDaConta: new FormControl('', Validators.required),
      numeroConta: new FormControl('')
    });
  }

  /**
   * Monta as opcoes do dropdown de moeda aplicando o filtro de pareamento
   * baseado na moeda padrao da empresa (recebida via @Input).
   */
  private buildMoedaOptions(): void {
    const permitidas = this.moedasPermitidas;
    this.moedaOptions = Object.values(Moeda)
      .filter(moeda => permitidas.has(String(moeda).toUpperCase()))
      .map(moeda => ({
        label: this.translate.instant(`enums.moeda.${moeda.toLowerCase()}`),
        value: String(moeda).toUpperCase(),
        flagUrl: this.getFlagUrl(moeda)
      }));
  }

  /** Retorna a URL da bandeira da moeda */
  private getFlagUrl(moeda: string): string {
    const moedaStr = String(moeda || '').toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  /** Retorna o locale conforme a moeda selecionada no form de nova conta */
  get novaContaLocale(): string {
    const moeda = this.novaContaForm?.get('moeda')?.value;
    if (!moeda) return 'es-PY';
    const moedaStr = String(moeda.value || moeda).toUpperCase();
    if (moedaStr === 'REAL') return 'pt-BR';
    if (moedaStr === 'DOLAR') return 'en-US';
    if (moedaStr === 'PESO') return 'es-AR';
    return 'es-PY';
  }

  /** Retorna as casas decimais conforme a moeda selecionada */
  get novaContaCasasDecimais(): number {
    const moeda = this.novaContaForm?.get('moeda')?.value;
    if (!moeda) return 0;
    const moedaStr = String(moeda.value || moeda).toUpperCase();
    return moedaStr === 'GUARANI' ? 0 : 2;
  }

  /** Retorna o simbolo da moeda selecionada para exibir no campo de saldo */
  get novaContaCurrencyPrefix(): string {
    const moeda = this.novaContaForm?.get('moeda')?.value;
    if (!moeda) return '';
    const moedaStr = String(moeda.value || moeda).toUpperCase();
    if (moedaStr === 'REAL') return 'R$ ';
    if (moedaStr === 'DOLAR') return '$ ';
    if (moedaStr === 'GUARANI') return 'Gs ';
    if (moedaStr === 'PESO') return '$ ';
    return '';
  }

  /** Retorna o numero de contas excedentes alem do limite de 10 no card */
  get contasExcedentes(): number {
    return Math.max(0, this.contas.length - 10);
  }

  /** Retorna o texto traduzido "e mais X conta(s)..." */
  get textoMaisContas(): string {
    return this.translate.instant('dashboard.texts.e_mais_contas', { count: this.contasExcedentes });
  }

  abrirDialogContas(): void {
    this.contasDialog = [...this.contas];
    this.showDialogContas = true;
  }

  irParaContas(): void {
    this.router.navigate(['/contas']);
  }

  irParaTransacoes(conta: ContaDTO): void {
    this.router.navigate(['/lista-de-movimentos'], {
      queryParams: { contaId: conta.id }
    });
  }

  /** Abre o dialog de nova conta */
  novaConta(): void {
    this.initNovaContaForm();
    this.preSelecionarMoedaPadrao();
    this.showDialogNovaConta = true;
  }

  /** Fecha o dialog de nova conta */
  fecharDialogNovaConta(): void {
    this.showDialogNovaConta = false;
    this.novaContaForm.reset();
  }

  /** Pre-seleciona a moeda padrao da empresa no formulario de nova conta */
  private preSelecionarMoedaPadrao(): void {
    const moedaEnum = (this.moedaPadraoEmpresa || 'REAL').toUpperCase();
    const moedaOption = this.moedaOptions.find(opt => opt.value === moedaEnum);
    if (moedaOption) {
      this.novaContaForm.get('moeda')?.setValue(moedaOption);
    }
  }

  /** Submete o formulario de nova conta */
  submitNovaConta(): void {
    // Marcar todos os campos como touched para mostrar validacoes
    Object.keys(this.novaContaForm.controls).forEach(key => {
      this.novaContaForm.get(key)?.markAsTouched();
    });

    if (this.novaContaForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.texts.campo_obrigatorio
      });
      return;
    }

    const formValue = this.novaContaForm.value;
    const moedaValue = formValue.moeda?.value || formValue.moeda;

    const body: ContaDTO = {
      moeda: moedaValue as Moeda,
      saldo: formValue.saldoInicial || 0,
      tituloDaConta: formValue.tituloDaConta,
      numeroConta: formValue.numeroConta || '',
      descricao: formValue.tituloDaConta,
      tipoConta: TipoConta.BANCO,
      valorTotalDeEntradas: 0,
      valorTotalDeSaidas: 0
    };

    this.salvandoConta = true;

    this.contaService.post('/conta', body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.conta_criada_sucesso
        });
        this.showDialogNovaConta = false;
        this.novaContaForm.reset();
        this.salvandoConta = false;
        this.contaCriada.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.erro_criar_conta
        });
        this.salvandoConta = false;
        this.cdr.markForCheck();
      }
    });
  }

  editarConta(conta: ContaDTO): void {
    this.showDialogContas = false;
    this.router.navigate(['/gerenciar-contas'], {
      queryParams: { editarId: conta.id }
    });
  }

  deletarConta(conta: ContaDTO): void {
    this.deleteItemId = conta.id ?? null;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao(): void {
    if (this.deleteItemId == null) return;

    const idParaExcluir = this.deleteItemId;

    this.contaService.delete(`/conta/${idParaExcluir}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Conta_excluida_com_sucesso'),
        });

        // Remover conta do dialog localmente
        this.contasDialog = this.contasDialog.filter(c => c.id !== idParaExcluir);
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
        this.contaExcluida.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('Erro_ao_excluir_a_conta'),
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
        this.cdr.markForCheck();
      },
    });
  }

  cancelarExclusao(): void {
    this.deleteDialogVisible = false;
    this.deleteItemId = null;
  }

  trackByContaId(index: number, conta: ContaDTO): number | undefined {
    return conta.id;
  }

  /** Retorna o texto traduzido do tipo da conta */
  tipoContaLabel(conta: ContaDTO): string {
    const tipo: any = conta.tipoConta;
    const enumValue = tipo?.enum ?? tipo ?? '';
    const key = String(enumValue).toLowerCase();
    return this.translate.instant(`enums.tipo_conta.${key}`);
  }

  /** Retorna simbolo da moeda baseado no enum */
  getCurrencySymbol(conta: ContaDTO): string {
    const m: any = conta.moeda;
    const e = m?.enum ?? m;
    const enumStr = String(e).toUpperCase();
    if (enumStr === 'REAL') return 'BRL';
    if (enumStr === 'DOLAR') return 'USD';
    if (enumStr === 'GUARANI') return 'PYG';
    return this.currencySymbol;
  }

  /** Retorna digitos do currency pipe conforme moeda */
  getCurrencyDigits(conta: ContaDTO): string {
    const m: any = conta.moeda;
    const e = m?.enum ?? m;
    const enumStr = String(e).toUpperCase();
    const casas = enumStr === 'GUARANI' ? 0 : 2;
    return `1.${casas}-${casas}`;
  }

  /** Retorna locale conforme moeda */
  getCurrencyLocale(conta: ContaDTO): string {
    const m: any = conta.moeda;
    const e = m?.enum ?? m;
    const enumStr = String(e).toUpperCase();
    if (enumStr === 'REAL') return 'pt-BR';
    if (enumStr === 'DOLAR') return 'en-US';
    if (enumStr === 'PESO') return 'es-AR';
    return 'es-PY';
  }

  /**
   * Formata valor monetario aplicando simbolo, locale e casas decimais por moeda.
   * Usado nas colunas de saldo/receitas/despesas. ARS delega ao CLDR `es-AR`
   * que produz `$ N,NN` (decisao tech lead 2026-05-06).
   */
  formatarValorMonetario(valor: number | null | undefined, conta: ContaDTO): string {
    if (valor == null || isNaN(valor)) return '-';

    const m: any = conta.moeda;
    const moedaStr = String(m?.enum ?? m ?? '').toUpperCase();

    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      'GUARANI': { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      'REAL': { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      'DOLAR': { locale: 'en-US', simbolo: '$', decimais: true },
      'PESO': { locale: 'es-AR', simbolo: '$', decimais: true },
    };

    const config = configuracoes[moedaStr] ?? configuracoes['GUARANI'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };

    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(valor);
    return `${config.simbolo} ${valorFormatado}`;
  }
}
